import { inject } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { FilterSelectItem } from '../../components/checkbox-facette/checkbox-liste/filterSelectItem';
import { Aggregations, DefaultAggregations } from '../../model/aggregations';
import { FilterAggregationsService } from '../filter-aggregations.service';
import { FacettenOptionInfo } from './facetten-option-info';
import { FacettenUrlService } from './facetten.url.service';
import { BasisFacetteService } from './instance/basis-facette.service';

export interface FacetteOption {
  id: string;
  anzahl: number;
}

export interface NamedFacetteOption extends FacetteOption {
  bezeichnung: string;
}

const getEnumValueByKey = (aggregations: DefaultAggregations, key: string): any => {
  return Object.entries(aggregations).find(([aggKey]) => aggKey === key)![1];
};

/**
 * Basis-Klasse für alle Services, die aus der Aggregation (nur mit ID, ohne Label) und den URL-Parametern
 * eine Liste an FilterSelectItems für die Checkbox-Liste in der Facette zusammenbauen.
 */
export abstract class FacettenService implements BasisFacetteService {
  protected _expanded$ = new BehaviorSubject<boolean>(false);
  public expanded$ = this._expanded$.asObservable();

  public selectedIds$ = this.facettenUrlService.selectedOptionIds$;

  protected filterAggregationsService = inject(FilterAggregationsService);
  protected filterAggregations$ = this.filterAggregationsService.filterAggregations$;

  protected constructor(
    protected facettenUrlService: FacettenUrlService,
    protected aggregationName: keyof Omit<
      Aggregations,
      'ANZAHL_AUSGEFILTERT' | 'ANZAHL_ANGEBOTE_GESAMT'
    >,
    protected orderedOptions: FacettenOptionInfo[]
  ) {}

  // Facetten-Optionen aus dem Backend
  private readonly _facetteOptions$: Observable<FacetteOption[]> = this.filterAggregations$.pipe(
    map((aggregations) =>
      Object.entries(this.getAggregation(aggregations) ?? {}).map(([id, anzahl]) => {
        return { id, anzahl } as FacetteOption;
      })
    )
  );

  // Facetten-Optionen kombiniert aus Backend und URL mit korrektem select-Status
  private readonly _selectItems$: Observable<FilterSelectItem[]> = combineLatest([
    this.getFacetteOptions$(),
    this.facettenUrlService.selectedOptionIds$
  ]).pipe(
    map(([facetteOptions, ids]) => {
      return this.orderedOptions.map(({ id, bezeichnung, modal, ariaLabel }) => {
        const anzahl = facetteOptions.find((option) => option.id === id.toString())?.anzahl ?? 0;
        const isSelected = this.isItemSelected(ids, id.toString());
        const item = {
          key: id.toString(),
          ariaLabel: ariaLabel ?? bezeichnung,
          treffer: anzahl,
          label: bezeichnung,
          selected: isSelected,
          active: anzahl > 0,
          modal
        };
        return item;
      });
    })
  );

  // Die Getter werden hier verwendet, um Subklassen zu ermöglichen,
  // eigene Logik zu implementieren. Es können nämlich Properties nicht überschrieben werden,
  // sondern nur Methoden.
  public getHasItems$(): Observable<boolean> {
    return this.getSelectItems$().pipe(
      map((items) => {
        const anzahlOptionen = items?.reduce<number>((acc, { treffer }) => acc + treffer, 0);
        return anzahlOptionen > 0;
      })
    );
  }

  public getAggregation(aggregations: DefaultAggregations) {
    return getEnumValueByKey(aggregations, this.aggregationName);
  }

  public getFacetteOptions$(): Observable<FacetteOption[]> {
    return this._facetteOptions$;
  }

  public getSelectItems$(): Observable<FilterSelectItem[]> {
    return this._selectItems$;
  }

  /**
   * Anzahl der ausgewählten, aktiven Optionen
   */
  public getNumberSelected$(): Observable<number> {
    return this.getSelectItems$().pipe(
      map((items) => items.filter(({ selected }) => selected).length)
    );
  }

  public getOrderedOptions(): FacettenOptionInfo[] {
    return [...this.orderedOptions];
  }

  // Setzt den expansion state der Facette
  public setExpanded(expanded: boolean): void {
    this._expanded$.next(expanded);
  }

  public isItemSelected(selectedIds: string[], key: string): boolean {
    return selectedIds.includes(key) || selectedIds.join(',') === key;
  }

  // Aktualisiert den ausgewählten Filter
  public updateSelectedIds(selectedIds: string[]): void {
    this.facettenUrlService.updateSelection(selectedIds);
  }

  public reset(): void {
    this.updateSelectedIds([]);
  }
}
